import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Badge
} from '@mui/material';
import {
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Timeline as TimelineIcon,
  Reply as ReplyIcon,
  Block as BlockIcon,
  Info as InfoIcon,
  FilterList as FilterListIcon,
  PieChart as PieChartIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { Lead } from '../types/lead';

// Tipos para o sistema de monitoramento de e-mails
export enum EmailStatus {
  SENT = 'sent',
  DELIVERED = 'delivered',
  OPENED = 'opened',
  UNREAD = 'unread',
  REPLIED = 'replied',
  BOUNCED = 'bounced',
  DISABLED = 'disabled',
  FAILED = 'failed'
}

interface EmailStatusEvent {
  status: EmailStatus;
  timestamp: Date;
  details: any;
}

interface EmailHistoryItem {
  _id: string;
  messageId: string;
  trackingId?: string;
  from: string;
  to: string;
  subject: string;
  content?: string;
  sentAt: Date;
  currentStatus: EmailStatus;
  statusHistory: EmailStatusEvent[];
  leadId?: Lead;
  metadata?: any;
  createdAt: Date;
  updatedAt: Date;
}

// Interface corrigida para estatísticas
interface EmailCounts extends Record<EmailStatus, number> {
  total: number;
}

interface EmailStatistics {
  counts: EmailCounts;
  rates: {
    openRate: number;
    bounceRate: number;
    replyRate: number;
    deliveryRate: number;
  };
}

// Componente para exibir o status do e-mail com ícone e cor apropriados
const EmailStatusChip: React.FC<{ status: EmailStatus }> = ({ status }) => {
  const getStatusConfig = (status: EmailStatus) => {
    switch (status) {
      case EmailStatus.SENT:
        return { label: 'Enviado', color: 'primary', icon: <EmailIcon fontSize="small" /> };
      case EmailStatus.DELIVERED:
        return { label: 'Entregue', color: 'info', icon: <CheckIcon fontSize="small" /> };
      case EmailStatus.OPENED:
        return { label: 'Lido', color: 'success', icon: <VisibilityIcon fontSize="small" /> };
      case EmailStatus.UNREAD:
        return { label: 'Não Lido', color: 'default', icon: <VisibilityOffIcon fontSize="small" /> };
      case EmailStatus.REPLIED:
        return { label: 'Respondido', color: 'success', icon: <ReplyIcon fontSize="small" /> };
      case EmailStatus.BOUNCED:
        return { label: 'Rejeitado', color: 'warning', icon: <WarningIcon fontSize="small" /> };
      case EmailStatus.DISABLED:
        return { label: 'Desativado', color: 'error', icon: <BlockIcon fontSize="small" /> };
      case EmailStatus.FAILED:
        return { label: 'Falha', color: 'error', icon: <ErrorIcon fontSize="small" /> };
      default:
        return { label: 'Desconhecido', color: 'default', icon: <InfoIcon fontSize="small" /> };
    }
  };

  const config = getStatusConfig(status);
  
  return (
    <Chip 
      icon={config.icon} 
      label={config.label} 
      color={config.color as any} 
      size="small" 
      variant="outlined"
    />
  );
};

// Componente de conteúdo de monitoramento de e-mails que pode ser reutilizado
export const EmailMonitoringContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [emails, setEmails] = useState<EmailHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalEmails, setTotalEmails] = useState(0);
  const [selectedEmail, setSelectedEmail] = useState<EmailHistoryItem | null>(null);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [statistics, setStatistics] = useState<EmailStatistics | null>(null);
  const [filterStatus, setFilterStatus] = useState<EmailStatus | ''>('');
  const [filterRecipient, setFilterRecipient] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  const leads = useSelector((state: RootState) => state.leads.items);

  // Carregar e-mails
  const loadEmails = async () => {
    setLoading(true);
    try {
      // Construir parâmetros de consulta
      const params = new URLSearchParams();
      params.append('page', (page + 1).toString());
      params.append('limit', rowsPerPage.toString());
      
      if (filterStatus) {
        params.append('status', filterStatus);
      }
      
      if (filterRecipient) {
        params.append('recipient', filterRecipient);
      }
      
      // Fazer requisição à API
      const response = await fetch(`/api/email/history?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error('Erro ao carregar histórico de e-mails');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setEmails(data.data.emails);
        setTotalEmails(data.data.total);
      } else {
        throw new Error(data.message || 'Erro ao carregar histórico de e-mails');
      }
    } catch (error) {
      console.error('Erro ao carregar e-mails:', error);
      
      // Carregar dados simulados para demonstração sem mostrar mensagem de erro
      loadMockData();
      
      // Comentado para não mostrar mensagem de erro em ambiente de desenvolvimento
      // setSnackbarMessage('Erro ao carregar histórico de e-mails. Tente novamente.');
      // setSnackbarSeverity('error');
      // setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // Carregar estatísticas
  const loadStatistics = async () => {
    try {
      const response = await fetch('/api/email/statistics');
      
      if (!response.ok) {
        throw new Error('Erro ao carregar estatísticas de e-mails');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setStatistics(data.data);
      } else {
        throw new Error(data.message || 'Erro ao carregar estatísticas de e-mails');
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      
      // Carregar estatísticas simuladas para demonstração
      const mockStats: EmailStatistics = {
        counts: {
          [EmailStatus.SENT]: 120,
          [EmailStatus.DELIVERED]: 110,
          [EmailStatus.OPENED]: 85,
          [EmailStatus.UNREAD]: 25,
          [EmailStatus.REPLIED]: 42,
          [EmailStatus.BOUNCED]: 8,
          [EmailStatus.DISABLED]: 2,
          [EmailStatus.FAILED]: 0,
          total: 120
        },
        rates: {
          openRate: 70.83,
          bounceRate: 6.67,
          replyRate: 35.00,
          deliveryRate: 91.67
        }
      };
      setStatistics(mockStats);
    }
  };

  // Carregar dados simulados para demonstração
  const loadMockData = () => {
    const mockEmails: EmailHistoryItem[] = Array(20).fill(null).map((_, index) => {
      const id = `email_${index + 1}`;
      const now = new Date();
      const sentDate = new Date(now.getTime() - Math.random() * 7 * 24 * 60 * 60 * 1000);
      
      // Distribuir status aleatoriamente para demonstração
      const statuses = Object.values(EmailStatus);
      const currentStatus = statuses[Math.floor(Math.random() * statuses.length)];
      
      // Criar histórico de status
      const statusHistory: EmailStatusEvent[] = [
        {
          status: EmailStatus.SENT,
          timestamp: sentDate,
          details: { initiatedBy: 'user_1' }
        }
      ];
      
      // Adicionar status DELIVERED se aplicável
      if (currentStatus !== EmailStatus.FAILED) {
        statusHistory.push({
          status: EmailStatus.DELIVERED,
          timestamp: new Date(sentDate.getTime() + 2 * 60 * 1000),
          details: { deliveryInfo: 'E-mail aceito pelo servidor SMTP' }
        });
      }
      
      // Adicionar status atual se diferente dos anteriores
      if (currentStatus !== EmailStatus.SENT && currentStatus !== EmailStatus.DELIVERED) {
        statusHistory.push({
          status: currentStatus,
          timestamp: new Date(sentDate.getTime() + (30 + Math.random() * 120) * 60 * 1000),
          details: {}
        });
      }
      
      return {
        _id: id,
        messageId: `msg_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
        trackingId: `trk_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
        from: 'contato@imobiliaria.com',
        to: `cliente${index + 1}@exemplo.com`,
        subject: `Assunto do e-mail ${index + 1}`,
        content: `<p>Conteúdo do e-mail ${index + 1}</p>`,
        sentAt: sentDate,
        currentStatus,
        statusHistory,
        leadId: leads.length > index ? leads[index] : undefined,
        createdAt: sentDate,
        updatedAt: new Date()
      };
    });
    
    setEmails(mockEmails);
    setTotalEmails(mockEmails.length);
  };

  useEffect(() => {
    loadEmails();
    loadStatistics();
  }, [page, rowsPerPage, filterStatus, filterRecipient]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDetails = (email: EmailHistoryItem) => {
    setSelectedEmail(email);
    setOpenDetailsDialog(true);
  };

  const handleCloseDetails = () => {
    setOpenDetailsDialog(false);
  };

  const handleRefresh = () => {
    loadEmails();
    loadStatistics();
    setSnackbarMessage('Dados atualizados com sucesso!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const clearFilters = () => {
    setFilterStatus('');
    setFilterRecipient('');
    setPage(0);
  };

  // Renderizar estatísticas
  const renderStatistics = () => {
    if (!statistics) return null;
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PieChartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Estatísticas de E-mails
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Total de E-mails:</Typography>
                  <Typography variant="h4">{statistics.counts.total}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Taxa de Abertura:</Typography>
                  <Typography variant="h4">{statistics.rates.openRate}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Taxa de Resposta:</Typography>
                  <Typography variant="h4">{statistics.rates.replyRate}%</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Taxa de Entrega:</Typography>
                  <Typography variant="h4">{statistics.rates.deliveryRate}%</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status dos E-mails
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={1}>
                {Object.entries(statistics.counts)
                  .filter(([key]) => key !== 'total')
                  .map(([status, count]) => (
                    <Grid item xs={6} key={status}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailStatusChip status={status as EmailStatus} />
                        <Typography variant="body1" sx={{ ml: 1 }}>
                          {count} ({((count / statistics.counts.total) * 100).toFixed(1)}%)
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Renderizar tabela de e-mails
  const renderEmailsTable = () => {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Destinatário</TableCell>
              <TableCell>Assunto</TableCell>
              <TableCell>Data de Envio</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={30} sx={{ my: 3 }} />
                </TableCell>
              </TableRow>
            ) : emails.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body1" sx={{ my: 3 }}>
                    Nenhum e-mail encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              emails.map((email) => (
                <TableRow key={email._id}>
                  <TableCell>{email.to}</TableCell>
                  <TableCell>{email.subject}</TableCell>
                  <TableCell>{new Date(email.sentAt).toLocaleString()}</TableCell>
                  <TableCell>
                    <EmailStatusChip status={email.currentStatus} />
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      size="small" 
                      onClick={() => handleOpenDetails(email)}
                      aria-label="Ver detalhes"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalEmails}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </TableContainer>
    );
  };

  // Renderizar filtros
  const renderFilters = () => {
    return (
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="status-filter-label">Filtrar por Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as EmailStatus | '')}
            label="Filtrar por Status"
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.values(EmailStatus).map((status) => (
              <MenuItem key={status} value={status}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EmailStatusChip status={status} />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <TextField
          label="Destinatário"
          variant="outlined"
          size="small"
          value={filterRecipient}
          onChange={(e) => setFilterRecipient(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        
        <Button 
          variant="outlined" 
          startIcon={<FilterListIcon />}
          onClick={clearFilters}
        >
          Limpar Filtros
        </Button>
        
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          sx={{ ml: 'auto' }}
        >
          Atualizar
        </Button>
      </Box>
    );
  };

  // Renderizar diálogo de detalhes
  const renderDetailsDialog = () => {
    if (!selectedEmail) return null;
    
    return (
      <Dialog 
        open={openDetailsDialog} 
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalhes do E-mail
          <IconButton
            aria-label="close"
            onClick={handleCloseDetails}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">De:</Typography>
              <Typography variant="body1" gutterBottom>{selectedEmail.from}</Typography>
              
              <Typography variant="subtitle2">Para:</Typography>
              <Typography variant="body1" gutterBottom>{selectedEmail.to}</Typography>
              
              <Typography variant="subtitle2">Assunto:</Typography>
              <Typography variant="body1" gutterBottom>{selectedEmail.subject}</Typography>
              
              <Typography variant="subtitle2">Data de Envio:</Typography>
              <Typography variant="body1" gutterBottom>
                {new Date(selectedEmail.sentAt).toLocaleString()}
              </Typography>
              
              <Typography variant="subtitle2">Status Atual:</Typography>
              <Box sx={{ mt: 1, mb: 2 }}>
                <EmailStatusChip status={selectedEmail.currentStatus} />
              </Box>
              
              {selectedEmail.leadId && (
                <>
                  <Typography variant="subtitle2">Lead Associado:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedEmail.leadId.name || 'Lead #' + selectedEmail.leadId.id}
                  </Typography>
                </>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Histórico de Status:</Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {selectedEmail.statusHistory.map((event, index) => (
                  <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < selectedEmail.statusHistory.length - 1 ? '1px solid #eee' : 'none' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmailStatusChip status={event.status} />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {new Date(event.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                    {event.details && Object.keys(event.details).length > 0 && (
                      <Typography variant="body2" color="textSecondary">
                        {JSON.stringify(event.details)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Conteúdo do E-mail:</Typography>
              <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                {selectedEmail.content ? (
                  <div dangerouslySetInnerHTML={{ __html: selectedEmail.content }} />
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Conteúdo não disponível
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Fechar</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box>
      <Tabs 
        value={activeTab} 
        onChange={handleChangeTab} 
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Visão Geral" />
        <Tab label="Lista de E-mails" />
      </Tabs>
      
      {activeTab === 0 && (
        <Box>
          {renderStatistics()}
        </Box>
      )}
      
      {activeTab === 1 && (
        <Box>
          {renderFilters()}
          {renderEmailsTable()}
        </Box>
      )}
      
      {renderDetailsDialog()}
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Página principal de monitoramento de e-mails (quando acessada diretamente)
const EmailMonitoringPage: React.FC = () => {
  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <TimelineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Monitoramento de E-mails
        </Typography>
        
        <EmailMonitoringContent />
      </Box>
    </Container>
  );
};

export default EmailMonitoringPage;
